/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalProps,
  Text,
  useInterval,
  useTimeout,
  VStack,
} from "@chakra-ui/react";
import useTranslate from "hooks/useTranslate";
import TraitsCanvas from "components/TraitsCanvas";
import ConfettiExplosion from "@reonomy/react-confetti-explosion";
import useApplyTraitChanges from "hooks/useApplyTraitsChanges";
import { useEffect, useMemo, useState } from "react";
import useRestoreCacheId from "hooks/useRestoreCacheId";

const SECONDS_TO_REFRESH = 15;

type TraitsChangedPopupProps = Omit<ModalProps, "children" | "onClose"> & {
  title: string;
  description: string;
  tokenId: number;
  currentTraits: number[];
  newTraits: number[];
  furColor: number;
  withConfetti?: boolean;
};

const TraitsChangedPopup = ({
  title,
  description,
  tokenId,
  furColor,
  currentTraits,
  newTraits,
  withConfetti,
  ...rest
}: TraitsChangedPopupProps) => {
  const translate = useTranslate();
  const restoreCacheId = useRestoreCacheId();
  const afterTraits = useApplyTraitChanges(currentTraits, newTraits);
  const [isReady, setIsReady] = useState(false);
  const [count, setCount] = useState<number>(SECONDS_TO_REFRESH);
  const [isCounting, setIsCounting] = useState<boolean>(true);

  useInterval(
    () => {
      if (count > 1) {
        setCount(count - 1);
      } else {
        setIsCounting(false);
      }
    },
    isCounting ? 1000 : null
  );

  useTimeout(() => {
    setIsReady(true);
  }, 1000);

  useEffect(() => {
    restoreCacheId();
  }, []);

  const celebration = useMemo(
    () =>
      isReady && withConfetti ? (
        <Box position="absolute" left="50%" top="4rem">
          <ConfettiExplosion
            floorWidth={800}
            floorHeight={1000}
            particleCount={70}
          />
        </Box>
      ) : null,
    [isReady, withConfetti]
  );

  return (
    <Modal onClose={() => {}} scrollBehavior="inside" {...rest}>
      <ModalOverlay />

      <ModalContent w="sm" mt="2rem" mb="2rem">
        {celebration}

        <ModalBody
          py="6"
          px="0"
          display="flex"
          flexDir="column"
          maxH="full"
          overflow="auto"
        >
          <VStack
            w="full"
            px="12"
            pt="2"
            spacing="2"
            align="stretch"
            textAlign="center"
          >
            <Heading fontSize="2xl" fontWeight="bold">
              {title}
            </Heading>

            <Text fontSize="sm" opacity="0.8">
              {description}
            </Text>
          </VStack>

          <Box pt="4">
            <TraitsCanvas
              traits={afterTraits}
              furColor={furColor}
              imagesSize="512"
              bg="dark.400"
            />
          </Box>

          <Box px="6" pt="6">
            <Box bg="dark.600" px="6" py="4" textAlign="center">
              <Text fontSize="sm" fontWeight="semibold" opacity="0.8">
                {translate("traitsChange:info")}
              </Text>
            </Box>

            <Button
              onClick={() => {
                window.location.href = `/bears/${tokenId}`;
              }}
              isDisabled={isCounting}
              w="full"
              colorScheme="secondary"
            >
              {isCounting ? count : translate("common:refresh")}
            </Button>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TraitsChangedPopup;
